import {
  PoiStatus,
  PointOfInterestMultiStructureInput,
  usePointOfInterestsCountByStatusQuery,
} from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { usePoiStatusLabels } from '@/hooks/usePoiStatusLabels';
import { Fade, Stack, Typography } from '@mui/material';
import { CountsList } from '../shared/CountsList';
import { StackedCount } from '../shared/StackedCount';
import { CustomSkeleton, Widget, getStatusColors } from '../styles';

// TODO: include in widget config in a mongo collection
const statusOrderKeys = [
  PoiStatus.Inreview,
  PoiStatus.ChaseItemAdded,
  PoiStatus.Inprogress,
  PoiStatus.PartiallyCompleted,
  PoiStatus.Completed,
  PoiStatus.Remediated,
];

export type PoiSummaryWidgetsProps = {
  queryInput: PointOfInterestMultiStructureInput;
  widgetWidth: string | number;
  riskDashboardPage?: boolean;
  legendTextColor?: string;
  poiType?: string;
};

export const StatusWidget = ({
  queryInput,
  widgetWidth = '0',
  poiType,
}: PoiSummaryWidgetsProps) => {
  const { loading, data } = usePointOfInterestsCountByStatusQuery({
    variables: { input: queryInput },
    notifyOnNetworkStatusChange: true,
  });
  const { getStatusOptions } = usePoiStatusLabels();

  const statusCounts = (() => {
    const statuses = data?.pointOfInterestsCountByStatus;

    if (!statuses) return [];
    const visibleOptions = getStatusOptions(poiType);
    return statusOrderKeys
      .filter((orderedKey) => visibleOptions.some((option) => option.value === orderedKey))
      .map((orderedKey) => {
        const exists = statuses.find(
          (unorderedStatus) => unorderedStatus?.key === orderedKey.toString()
        );
        const label =
          visibleOptions.find((option) => option.value === orderedKey)?.label ?? orderedKey;
        return {
          key: label,
          enumKey: orderedKey,
          count: exists?.count ?? 0,
        };
      });
  })();

  return (
    <Widget loading={loading} width={widgetWidth}>
      {loading && (
        <>
          <CustomSkeleton animation="wave" width="5.6rem" />
          <CustomSkeleton animation="wave" width="26.8rem" />
          {Array.from({ length: 5 }, (_, index) => (
            <Stack key={index} direction="row" alignItems="center" justifyContent="space-between">
              <CustomSkeleton animation="wave" width="18.1rem" />
              <CustomSkeleton animation="wave" width="3.5rem" />
            </Stack>
          ))}
        </>
      )}
      {!loading && (
        <Fade in>
          <Stack gap={'1.6rem'}>
            <Typography fontSize="1.4rem" fontWeight={500} color={abyssColors.primary[500]}>
              Status
            </Typography>
            <StackedCount counts={statusCounts} colorMapper={getStatusColors} />
            <CountsList counts={statusCounts} colorMapper={getStatusColors} />
          </Stack>
        </Fade>
      )}
    </Widget>
  );
};
